import React from "react";
import { Card, CardContent } from "../../ui/Card";
import { Badge } from "../../ui/Badge";
import {
  Info,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Lightbulb,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

export type CalloutType = "info" | "warning" | "error" | "success" | "tip";

interface DocsCalloutProps {
  type?: CalloutType;
  children: React.ReactNode;
  className?: string;
}

const calloutConfig: Record<
  CalloutType,
  {
    icon: React.ComponentType<{ className?: string }>;
    bgColor: string;
    borderColor: string;
    textColor: string;
  }
> = {
  info: {
    icon: Info,
    bgColor: "bg-[var(--su-gray-800)]",
    borderColor: "border-[var(--su-purple-400)]",
    textColor: "text-[var(--su-purple-400)]",
  },
  warning: {
    icon: AlertTriangle,
    bgColor: "bg-[var(--su-amber-900)]",
    borderColor: "border-[var(--su-amber-400)]",
    textColor: "text-[var(--su-amber-400)]",
  },
  error: {
    icon: XCircle,
    bgColor: "bg-[var(--su-red-700)]",
    borderColor: "border-[var(--su-red-400)]",
    textColor: "text-[var(--su-red-400)]",
  },
  success: {
    icon: CheckCircle,
    bgColor: "bg-[var(--su-green-900)]",
    borderColor: "border-[var(--su-green-400)]",
    textColor: "text-[var(--su-green-400)]",
  },
  tip: {
    icon: Lightbulb,
    bgColor: "bg-[var(--su-gray-800)]",
    borderColor: "border-[var(--su-purple-accent)]",
    textColor: "text-[var(--su-purple-accent)]",
  },
};

export function DocsCallout({
  type = "info",
  children,
  className,
}: DocsCalloutProps) {
  const config = calloutConfig[type];
  const Icon = config.icon;

  return (
    <Card
      className={cn(
        "rounded-none border-2 my-6",
        config.bgColor,
        config.borderColor,
        className,
      )}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          <Icon
            className={cn("w-5 h-5 flex-shrink-0 mt-0.5", config.textColor)}
          />
          <div className="flex-1 prose prose-invert max-w-none">
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{
                p: ({ children }) => (
                  <p className="m-0 text-[var(--su-text-primary)]">
                    {children}
                  </p>
                ),
                a: ({ href, children }) => {
                  const isExternal =
                    href?.startsWith("http") || href?.startsWith("mailto:");
                  return (
                    <a
                      href={href}
                      className="text-[var(--su-purple-accent)] hover:underline"
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                    >
                      {children}
                    </a>
                  );
                },
                strong: ({ children }) => (
                  <strong className="font-semibold text-[var(--su-text-primary)]">
                    {children}
                  </strong>
                ),
                code: ({ children, className: codeClassName }) => {
                  const isInline = !codeClassName;
                  return isInline ? (
                    <code className="px-1.5 py-0.5 bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] text-[var(--su-purple-accent)] text-sm rounded-none font-mono">
                      {children}
                    </code>
                  ) : (
                    <code className={codeClassName}>{children}</code>
                  );
                },
                em: ({ children }) => (
                  <em className="italic text-[var(--su-text-primary)]">
                    {children}
                  </em>
                ),
                ul: ({ children }) => (
                  <ul className="mt-2 mb-0 ml-4 list-disc">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="mt-2 mb-0 ml-4 list-decimal">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="text-[var(--su-text-primary)]">{children}</li>
                ),
              }}
            >
              {typeof children === "string" ? children : String(children)}
            </ReactMarkdown>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
